/**
 * PORT-008 §8.5 — **le registre UNIQUE des entrées de menu d'un espace.**
 *
 * Moule `config/navigation.ts`. `PortalSidebar` rend une entrée — du socle, ou
 * reçue par `entreesDeFacette` — **si et seulement si** `entreeEstMontee`
 * l'accepte : un contexte actif existe, **et** `kind ∈ facets`, **et**
 * (`capability` absente ou présente dans `effective_capabilities` — JAMAIS
 * `capabilities`), **et** (`instructorScope` absent ou égal à
 * `instructor_scope`), **et** (`requiresFlag` absent ou drapeau à `true`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CE QUE LE SOCLE ÉCRIT, ET CE QU'IL N'ÉCRIT PAS
 * ═══════════════════════════════════════════════════════════════════════════
 * Le socle n'écrit que le **tronc commun** — Accueil, Mes sessions, Mon compte.
 * Les entrées de **facette** appartiennent au ticket de leur écran (LRNP-002,
 * TRNP-002 → 016, CPORT-004 → 020), et la liste de ces écrivains est
 * **ouverte** : ne jamais la refermer sur un total, ni la recopier ici.
 *
 * ⛔ **Pas de `labelByFacet`.** Une entrée porte UN libellé, quelles que soient
 * ses facettes (arbitrage du 2026-09-06, désaccord à trois voix sur « Mes
 * sessions » / « Planning » / « Mon planning »). **Le titre de la page est le
 * mot du menu.**
 *
 * ⚠️ **`instructorScope` est camelCase parce que c'est une clé de REGISTRE** ;
 * la valeur qu'elle compare, `active_context.instructor_scope`, reste
 * snake_case — miroir strict du Pydantic. Mesuré le 2026-09-16 (sonde `tsc`,
 * puis exécution du prédicat) : `tsc` refuse `instructor_scope` dans une entrée
 * LITTÉRALE (TS2561) et `contexte.instructorScope` ici (TS2551). Il ne dit rien
 * d'une entrée construite ailleurs puis affectée : la clé snake_case y passe,
 * `instructorScope` vaut `undefined`, la garde est sautée, et l'entrée monte
 * **par accident** pour TOUT intervenant.
 *
 * ⚠️ `PortalContextFlag` est une union **écrite à la main**, PAS dérivée de
 * `/me` : rien ne la DÉRIVE du miroir `PortalContext` (`usePortalMe.types.ts`),
 * et aucun test ne la compare au serveur. Le seul lien est l'appel de
 * `PortalSidebar`, qui passe un `PortalContext` : un drapeau DÉCLARÉ par le
 * miroir doit y être booléen (TS2345 sinon), sans que rien vérifie qu'il y soit
 * déclaré ni servi. Mesuré le 2026-09-16 par une sonde `tsc` :
 * · un nom HORS de cette liste ne compile pas (TS2322, ou TS2820 quand le nom
 *   ressemble à un nom de la liste) ;
 * · un nom DE cette liste compile, servi ou non : `has_settable_notification_types`
 *   y figure, le miroir ne le déclare pas, `/api/portal/me` ne le sert pas, et
 *   une entrée qui l'invoque compile ;
 * · le paramètre `contexte` de `entreeEstMontee` est un type écrit ici, pas
 *   `PortalContext` : `{ kind: 'LEARNER' }` y est accepté.
 * Ce qui empêche une garde de monter sur un champ non servi n'est donc pas le
 * compilateur, c'est l'EXÉCUTION : la défaillance fermée (`!== true`) plus bas.
 * Élargir l'union n'exige rien du serveur — le servir reste à la charge de qui élargit.
 */

import {
    CalendarDays,
    Home,
    UserRound,
    type LucideIcon,
} from 'lucide-react'

import type { PortalContextKind } from '@/hooks/usePortalMe.types'

/** Liste écrite à la main, NON dérivée de `/me` — voir l'en-tête. L'élargir n'exige rien du serveur. */
export type PortalContextFlag =
    | 'has_other_learners'
    | 'has_settable_notification_types'
    | 'has_document_capability'

export interface PortalNavItem {
    icon: LucideIcon
    /** Clé i18n. Déclarée en FR **et** EN, sans quoi l'anglais rend le français. */
    label: string
    href: string
    facets: PortalContextKind[]
    capability?: string
    /**
     * L'écran attend un module non livré. L'entrée reste **visible** et son
     * écran explique l'attente : la masquer ferait croire à une capacité
     * absente, alors que c'est le module qui manque.
     */
    pending?: 'SESSIONS'
    requiresFlag?: PortalContextFlag
    instructorScope?: 'PERSON' | 'ORG'
    badge?: () => number | null
}

const TOUTES: PortalContextKind[] = ['CONTACT', 'INSTRUCTOR', 'LEARNER', 'PARTICULIER']

/**
 * Le **tronc commun** : trois entrées, toutes facettes, aucune capacité.
 *
 * ⚠️ « Mon compte » a une destination RÉELLE (`/espace/app/compte`) depuis le
 * 2026-09-07. Elle a longtemps eu un libellé, une icône et **aucune page** —
 * sa page étant attendue de trois tickets hors de cet EPIC. Un menu qui monte
 * une entrée qui ne s'ouvre pas est pire qu'un menu court.
 */
export const PORTAL_NAV_SOCLE: PortalNavItem[] = [
    { icon: Home, label: 'portal.nav.home', href: '/espace/app', facets: TOUTES },
    {
        icon: CalendarDays,
        label: 'portal.nav.sessions',
        href: '/espace/app/sessions',
        facets: TOUTES,
        pending: 'SESSIONS',
    },
    { icon: UserRound, label: 'portal.nav.account', href: '/espace/app/compte', facets: TOUTES },
]

/**
 * Le prédicat de montage — **la seule** règle, appliquée à toutes les entrées.
 *
 * Un `if` dispersé dans la barre latérale serait la porte ouverte à une garde
 * par entrée : c'est ce que ce registre existe pour empêcher.
 */
export function entreeEstMontee(
    entree: PortalNavItem,
    contexte: ({
        kind: PortalContextKind
        effective_capabilities?: string[]
        instructor_scope?: 'PERSON' | 'ORG' | null
    } & Partial<Record<PortalContextFlag, boolean>>) | null,
): boolean {
    if (contexte === null) return false
    if (!entree.facets.includes(contexte.kind)) return false

    if (entree.capability) {
        // 🔴 `effective_capabilities`, JAMAIS `capabilities`. La colonne brute
        // vaut `null` quand les capacités « suivent les rôles » — la lire ici
        // fermerait tout à un contact principal, qui est précisément celui à
        // qui tout doit être ouvert sans le moindre réglage.
        if (!contexte.effective_capabilities?.includes(entree.capability)) return false
    }

    if (entree.instructorScope && contexte.instructor_scope !== entree.instructorScope) {
        return false
    }

    if (entree.requiresFlag) {
        // ⚠️ **Défaillance FERMÉE, et c'est délibéré.** `/api/portal/me` sert
        // `has_document_capability` (PORT-011) et, depuis le 2026-09-16,
        // `has_other_learners` (A14, `backend/src/api/portal/routes_me.py`). Il ne sert pas
        // `has_settable_notification_types` (attend NOTIF-003 puis PORT-005 §7.1) :
        // une entrée qui l'invoque compile, et ne se monte pas.
        //
        // ⚠️ Servir `has_other_learners` ne fait PAS monter « Nos stagiaires ».
        // Mesuré le 2026-09-16 : hors de ce fichier, `requiresFlag` n'est lu que
        // par deux tests ; `PortalLayout` rend `PortalSidebar` sans
        // `entreesDeFacette` ; ni la route `/espace/app/stagiaires` ni sa page
        // n'existent (CPORT-005, « À implémenter »). Le mécanisme est gardé par
        // `__tests__/nosStagiairesSuitLeDrapeauServi.test.tsx`, sur une entrée que
        // le test fabrique — aucune entrée livrée ne le traverse.
        //
        // Le sens de la garde compte : `!== true` refuse aussi bien `false` que
        // `undefined`. Traiter l'absence comme un « oui » monterait, le jour où
        // le champ manque, une entrée vers un écran qui refusera — et le
        // reproche irait à l'écran, pas au menu.
        if (contexte[entree.requiresFlag] !== true) return false
    }

    return true
}
